// ShareBar.tsx
import React, { useState } from 'react';
import styled from 'styled-components';
import {
  FacebookShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  EmailShareButton,
  LinkedinShareButton,
  RedditShareButton,
  FacebookIcon,
  TwitterIcon,
  WhatsappIcon,
  EmailIcon,
  LinkedinIcon,
  RedditIcon,
  XIcon,
} from 'react-share';
import { onHoverHistogram, useGlobalHoveredId, useGlobalSelectedId, useGlobalActivePage, useGlobalActiveGeography, useGlobalActiveIndicator, useGlobalBivariateIndicator } from '../../data/StatusStore';
import posthog from 'posthog-js';
/* ------------------------------------------------------------------ */
/* 1.  Share data                                                      */
/* ------------------------------------------------------------------ */
const shareUrl = window.location.href;
const title = 'Check out DATA2GO.NYC!';

/* ------------------------------------------------------------------ */
/* 2.  Icon constants                                                  */
/* ------------------------------------------------------------------ */
const ICON_SIZE = 25;
const ICON_FILL = '#231f20';
const BG_STYLE = {
  fill: '#D3D3D3',
  stroke: '#231f20',
  strokeWidth: 4,
};

/* ------------------------------------------------------------------ */
/* 3.  Styled Wrappers                                                 */
/* ------------------------------------------------------------------ */
const IconWrapper = styled.span`
  display: inline-block;
  padding: 0px;
  border-radius: 50%;
  line-height: 0;
  background: transparent;

  & svg path,
  & svg rect,
  & svg circle {
    transition: fill 0.2s ease, stroke 0.2s ease;
  }

  &:hover svg path {
    fill: #a29f9f !important;
  }
  &:hover svg rect,
  &:hover svg circle {
    stroke: #a29f9f !important;
  }
`;

const ManualCopyIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${ICON_SIZE}px;
  height: ${ICON_SIZE}px;
  border-radius: 50%;
  background-color: ${BG_STYLE.fill};
  border: 2px solid ${BG_STYLE.stroke};
  cursor: pointer;

  svg {
    width: 60%;
    height: 60%;
    color: ${ICON_FILL};
    transition: color 0.2s ease;
  }

  &:hover svg {
    color: #a29f9f;
  }
`;

/* ------------------------------------------------------------------ */
/* 4.  Component                                                       */
/* ------------------------------------------------------------------ */
const ShareBar: React.FC = () => {
  const [copied, setCopied] = useState(false);
  type SharePlatform = 'facebook' | 'linkedin' | 'whatsapp' | 'x' | 'twitter' | 'email' | 'reddit';
  const activeGeography = useGlobalActiveGeography();
  const selectedId = useGlobalSelectedId();
  const activePage = useGlobalActivePage();
  const activeIndicator = useGlobalActiveIndicator();
  const bivariateIndicator = useGlobalBivariateIndicator();
  // Call this when a share is initiated/completed
  const handleShare = (platform: SharePlatform, url: string) => {
    // Replace/extend with your analytics of choice
    // Example gtag:
    // window.gtag?.('event', 'share', { method: platform, content_type: 'link', item_id: url });

    // Also emit a custom event apps can listen to
    //window.dispatchEvent(new CustomEvent('share', { detail: { platform, url } }));

    // Fallback log
    console.log('A121525 share', { platform, url });
    const phParams = {
      event_class: 'link_shared',
      platform: platform,
      url: url,
      view: activePage,
      geography_type: activeGeography,
      geography_id: selectedId,
      indicator: activeIndicator,
      bivariate_indicator: bivariateIndicator,
    };
    posthog.capture('ui_event', phParams);
    console.log('A121525 PostHog event: ui_event', phParams);
  };

  // Helper to attach to onClick/onShareWindowClose
  const onShare = (platform: SharePlatform) => () => handleShare(platform, shareUrl);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={{ lineHeight: '15px' }}>Share!</div>
      <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <FacebookShareButton url={shareUrl} title={title} onClick={onShare('facebook')}>
          <IconWrapper>
            <FacebookIcon size={ICON_SIZE} iconFillColor={ICON_FILL} bgStyle={BG_STYLE} />
          </IconWrapper>
        </FacebookShareButton>

        <LinkedinShareButton url={shareUrl} title={title} onClick={onShare('linkedin')}>
          <IconWrapper>
            <LinkedinIcon size={ICON_SIZE} iconFillColor={ICON_FILL} bgStyle={BG_STYLE} />
          </IconWrapper>
        </LinkedinShareButton>

        <WhatsappShareButton url={shareUrl} title={title} onClick={onShare('whatsapp')}>
          <IconWrapper>
            <WhatsappIcon size={ICON_SIZE} iconFillColor={ICON_FILL} bgStyle={BG_STYLE} />
          </IconWrapper>
        </WhatsappShareButton>

        <TwitterShareButton url={shareUrl} title={title} hashtags={['data2go']} onClick={onShare('twitter')}>
          <IconWrapper>
            <XIcon size={ICON_SIZE} iconFillColor={ICON_FILL} bgStyle={BG_STYLE} />
          </IconWrapper>
        </TwitterShareButton>

        <EmailShareButton url={shareUrl} subject={title} body={`${title} — ${shareUrl}`} beforeOnClick={onShare('email')} >
          <IconWrapper>
            <EmailIcon size={ICON_SIZE} iconFillColor={ICON_FILL} bgStyle={BG_STYLE} />
          </IconWrapper>
        </EmailShareButton>

        {/*<RedditShareButton url={shareUrl} title={title}>
          <IconWrapper>
            <RedditIcon size={ICON_SIZE} iconFillColor={ICON_FILL} bgStyle={BG_STYLE} />
          </IconWrapper>
        </RedditShareButton>*/}
      </div>
    </div>
  );
};

export default ShareBar;
